import { qboCharges, allCharges } from '../config/constants'

const normalizeQboText = (value) => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim().toLowerCase()
  return trimmed.replace(/\u2013|\u2014/g, '-').replace(/\s+/g, ' ')
}

// Map QBO line type (and description as fallback) onto one of the labels in allCharges.
const mapQboLineTypeToChargeLabel = (lineType, lineDescription) => {
  const normalizedLineType = normalizeQboText(lineType || '')
  const normalizedDescription = normalizeQboText(lineDescription || '')
  const canonicalLabels = Array.isArray(allCharges) ? allCharges : Object.values(qboCharges || {})

  if (!normalizedLineType && !normalizedDescription) return null

  for (const label of canonicalLabels) {
    if (normalizeQboText(label) === normalizedLineType) return label
  }

  const chargeEnumEntries = Object.entries(qboCharges || {})
  for (const [enumKey, label] of chargeEnumEntries) {
    const keyAsWords = normalizeQboText(enumKey.replace(/_/g, ' '))
    if (normalizedLineType === keyAsWords || normalizedLineType === enumKey.toLowerCase()) return label
  }

  const pickBestLabelMatch = (normalizedInput, candidateLabels) => {
    if (!normalizedInput) return null
    let bestLabel = null
    let bestLabelLength = -1
    for (const label of candidateLabels) {
      const normalizedLabel = normalizeQboText(label)
      if (!normalizedLabel) continue
      if (normalizedInput === normalizedLabel) return label
      if (normalizedInput.includes(normalizedLabel) || normalizedLabel.includes(normalizedInput)) {
        if (normalizedLabel.length > bestLabelLength) {
          bestLabelLength = normalizedLabel.length
          bestLabel = label
        }
      }
    }
    return bestLabel
  }

  const matchFromLineType = normalizedLineType
    ? pickBestLabelMatch(normalizedLineType, canonicalLabels)
    : null
  if (matchFromLineType) return matchFromLineType

  const matchFromDescription = normalizedDescription
    ? pickBestLabelMatch(normalizedDescription, canonicalLabels)
    : null
  return matchFromDescription || null
}

const matchLineTypeOrDescriptionToCanonical = (normalizedInput, canonicalLabels, chargeEnumEntries) => {
  if (!normalizedInput) return null
  for (const label of canonicalLabels) {
    if (normalizeQboText(label) === normalizedInput) return label
  }
  for (const [enumKey, label] of chargeEnumEntries) {
    const keyAsWords = normalizeQboText(enumKey.replace(/_/g, ' '))
    if (normalizedInput === keyAsWords || normalizedInput === enumKey.toLowerCase()) return label
  }
  return null
}

/**
 * Exact / enum-key matching on **line type only** (charge name / description ignored).
 * No fuzzy substring rules. Empty line type ⇒ null.
 */
const mapQboLineTypeToChargeLabelStrict = (lineType) => {
  const normalizedLineType = normalizeQboText(lineType || '')
  if (!normalizedLineType) return null
  const canonicalLabels = Array.isArray(allCharges) ? allCharges : Object.values(qboCharges || {})
  const chargeEnumEntries = Object.entries(qboCharges || {})
  return matchLineTypeOrDescriptionToCanonical(normalizedLineType, canonicalLabels, chargeEnumEntries)
}

/** True when charge line text is non-empty and not the literal UNDEFINED QBO placeholder. */
const qboChargeNameIsMeaningful = (chargeName) => {
  if (typeof chargeName !== 'string') return false
  const t = chargeName.trim()
  if (!t) return false
  return normalizeQboText(t) !== 'undefined'
}

const qboChargeRowDedupeKey = (row) =>
  [
    row?.clientName ?? '',
    String(row?.invoiceNumber ?? '').trim(),
    String(row?.lineType ?? '').trim(),
    row?.billingMonthKey ?? '',
    Number(row?.amount) || 0
  ].join('\0')

/**
 * Collapse duplicate QBO charge rows per month bucket / billing list: same client, invoice #, line type,
 * bucket month, and amount → keep one row; prefer a meaningful chargeName over the UNDEFINED placeholder.
 */
const dedupeQboChargeRows = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? rows : []
  const byKey = new Map()
  for (const row of rows) {
    const key = qboChargeRowDedupeKey(row)
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, row)
      continue
    }
    if (qboChargeNameIsMeaningful(row.chargeName) && !qboChargeNameIsMeaningful(prev.chargeName)) {
      byKey.set(key, row)
    }
  }
  return [...byKey.values()]
}

export {
  mapQboLineTypeToChargeLabel,
  mapQboLineTypeToChargeLabelStrict,
  normalizeQboText as normalizeQboChargeString,
  qboChargeNameIsMeaningful,
  qboChargeRowDedupeKey,
  dedupeQboChargeRows
}
